type ChromeTone = "light" | "dark";

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

export function resolveChromeSurfaceToneAtPoint(
  chrome: HTMLElement,
  x: number,
  y: number,
) {
  const elements = document.elementsFromPoint(
    clamp(x, 2, window.innerWidth - 2),
    clamp(y, 2, window.innerHeight - 2),
  );

  for (const element of elements) {
    if (!(element instanceof HTMLElement)) {
      continue;
    }

    if (chrome.contains(element)) {
      continue;
    }

    if (element.classList.contains("venuex-navigation")) {
      continue;
    }

    const computedStyle = window.getComputedStyle(element);

    if (
      computedStyle.display === "none" ||
      computedStyle.visibility === "hidden" ||
      Number.parseFloat(computedStyle.opacity || "1") < 0.04
    ) {
      continue;
    }

    const surfaceNode = element.closest<HTMLElement>("[data-chrome-surface]");

    if (surfaceNode?.dataset.chromeSurface === "dark") {
      return "dark" as const;
    }

    if (surfaceNode?.dataset.chromeSurface === "light") {
      return "light" as const;
    }

    const sectionNode = element.closest<HTMLElement>("[data-stage-section]");

    if (sectionNode?.dataset.stageChromeTheme === "dark") {
      return "dark" as const;
    }

    if (sectionNode?.dataset.stageChromeTheme === "light") {
      return "light" as const;
    }
  }

  return "light" as const;
}

export function applyChromeSplitTone(
  chrome: HTMLElement,
  control: HTMLElement | null,
) {
  if (!control) {
    return;
  }

  const rect = control.getBoundingClientRect();
  const sampleCount = 24;
  const tones: ChromeTone[] = [];

  for (let index = 0; index < sampleCount; index += 1) {
    const progress = index / (sampleCount - 1);
    const sampleX = rect.left + rect.width * progress;
    const sampleY = rect.top + rect.height * 0.5;
    const surfaceTone = resolveChromeSurfaceToneAtPoint(chrome, sampleX, sampleY);
    tones.push(surfaceTone === "dark" ? "light" : "dark");
  }

  if (tones.every((tone) => tone === "light")) {
    control.dataset.lightCoverage = "full";
    control.style.setProperty("--chrome-split-ratio", "100%");
    return;
  }

  if (tones.every((tone) => tone === "dark")) {
    control.dataset.lightCoverage = "none";
    control.style.setProperty("--chrome-split-ratio", "0%");
    return;
  }

  let splitIndex = tones.findIndex(
    (tone, index) => index > 0 && tone !== tones[index - 1],
  );

  if (splitIndex === -1) {
    splitIndex = Math.floor(sampleCount * 0.5);
  }

  const firstTone = tones[0];
  const lastTone = tones[tones.length - 1];
  const ratio = (splitIndex / (sampleCount - 1)) * 100;

  control.style.setProperty("--chrome-split-ratio", `${ratio}%`);
  control.dataset.lightCoverage =
    firstTone === "light" && lastTone === "dark" ? "left" : "right";
}
